<template>
    <!-- Top Bar with Logo and Toggle -->
    <div class="top-bar">
        <div class="top-bar-start">
            <p-button 
                icon="pi pi-bars" 
                @click="toggleSidebar"
                text
                rounded
                class="sidebar-toggle"
            />
            <a href="/" class="logo-container">
                <img alt="logo" src="../assets/logo.png" height="40" />
            </a>
        </div>
        
        <div class="top-bar-end">
            <Halo :env="env" :home_url="home_url" />
            <div class="user-section">
                <!--<span class="user-name">{{ user.name }}</span> -->
                <p-button
                    type="button"
                    :label="user.role"
                    @click="sandboxDialog = true"
                    icon="pi pi-user"
                    class="role-btn"
                    text
                />
                <p-button
                    type="button"
                    label="Help"
                    icon="pi pi-question-circle"
                    class="help-btn"
                    @click="openUserManual"
                    text
                    v-tooltip="'User Manual & Help'"
                />
                <p-button
                    type="button"
                    label="Logout"
                    icon="pi pi-sign-out"
                    class="logout-btn"
                    @click="logout"
                    severity="danger"
                    text
                />
            </div>
        </div>
    </div>

    <!-- Sidebar -->
    <div class="sidebar" :class="{ 'sidebar-collapsed': !sidebarVisible }">
        <div class="sidebar-content">
            <!-- Main Menu Items -->
            <div class="menu-section">
                <router-link 
                    v-for="item in mainMenuItems" 
                    :key="item.label"
                    :to="item.to"
                    class="menu-item"
                    :class="{ 'active': $route.path === item.to }"
                    @click="sidebarVisible = false"
                >
                    <i :class="item.icon"></i>
                    <span class="menu-label">{{ item.label }}</span>
                </router-link>
            </div>

            <!-- Admin Section -->
            <div v-if="isAdminUser" class="menu-section">
                <div class="menu-header">
                    <i class="pi pi-fw pi-wrench"></i>
                    <span class="menu-label">Admin</span>
                    <i 
                        class="pi pi-fw pi-angle-down toggle-icon" 
                        :class="{ 'expanded': adminExpanded }"
                        @click="adminExpanded = !adminExpanded"
                    ></i>
                </div>
                <div class="submenu" :class="{ 'submenu-collapsed': !adminExpanded }">
                    <router-link 
                        v-for="item in adminMenuItems" 
                        :key="item.label"
                        :to="item.to"
                        class="menu-item submenu-item"
                        :class="{ 'active': $route.path === item.to }"
                        @click="sidebarVisible = false"
                    >
                        <i :class="item.icon"></i>
                        <span class="menu-label">{{ item.label }}</span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>

    <!-- Sidebar Overlay for Mobile -->
    <div 
        v-if="sidebarVisible" 
        class="sidebar-overlay"
        @click="sidebarVisible = false"
    ></div>

    <p-dialog  v-model:visible="sandboxDialog" modal header="Edit Sandbox User" :style="{ width: '700px' }">
        <SandBox />
    </p-dialog>

    <!-- Help Dialog -->
    <p-dialog v-model:visible="showHelpDialog" modal header="SimForce User Manual & Help" :style="{ width: '90vw', height: '90vh' }">
        <div class="help-content">
            <div class="grid">
                <div class="col-12 md:col-4">
                    <div class="help-section">
                        <h4>🚀 Getting Started</h4>
                        <ul>
                            <li><strong>Login:</strong> Use your company email and password</li>
                            <li><strong>Dashboard:</strong> Overview of your leave balances and recent activity</li>
                            <li><strong>Apply Leave:</strong> Click "Apply for Leave" for standard requests</li>
                            <li><strong>AI Apply:</strong> Use "AI Apply" for smart recommendations</li>
                        </ul>
                    </div>
                    
                    <div class="help-section">
                        <h4>🤖 AI Features</h4>
                        <ul>
                            <li><strong>Smart Validation:</strong> Real-time leave request analysis</li>
                            <li><strong>Approval Prediction:</strong> Likelihood of approval based on history</li>
                            <li><strong>Risk Assessment:</strong> Team impact and business risk factors</li>
                            <li><strong>Date Recommendations:</strong> Optimal timing suggestions</li>
                        </ul>
                    </div>
                </div>
                
                <div class="col-12 md:col-4">
                    <div class="help-section">
                        <h4>📝 Leave Application Process</h4>
                        <ol>
                            <li>Navigate to <strong>Leave Management</strong></li>
                            <li>Click <strong>"Apply for Leave"</strong></li>
                            <li>Select leave type and dates</li>
                            <li>Use <strong>"AI Analysis"</strong> for recommendations</li>
                            <li>Fill in reason and contact details</li>
                            <li>Submit your application</li>
                        </ol>
                    </div>
                    
                    <div class="help-section">
                        <h4>👨‍💼 For Managers</h4>
                        <ul>
                            <li><strong>Pending Approvals:</strong> Review team leave requests</li>
                            <li><strong>AI Insights:</strong> Use AI recommendations for decisions</li>
                            <li><strong>Team Calendar:</strong> View team availability</li>
                            <li><strong>Delegation:</strong> Set up proxy approvers</li>
                        </ul>
                    </div>
                </div>
                
                <div class="col-12 md:col-4">
                    <div class="help-section">
                        <h4>⚙️ Admin Features</h4>
                        <ul>
                            <li><strong>Leave Rules:</strong> Configure business validation rules</li>
                            <li><strong>Approval Flows:</strong> Set up approval workflows</li>
                            <li><strong>Analytics:</strong> Monitor system performance</li>
                            <li><strong>User Management:</strong> Manage employee accounts</li>
                        </ul>
                    </div>
                    
                    <div class="help-section">
                        <h4>📱 Mobile App</h4>
                        <ul>
                            <li><strong>Leave Dashboard:</strong> Quick access to balances</li>
                            <li><strong>AI Features:</strong> Smart leave applications</li>
                            <li><strong>Notifications:</strong> Real-time approval updates</li>
                            <li><strong>Offline Support:</strong> Basic functionality without internet</li>
                        </ul>
                    </div>
                </div>
                
                <div class="col-12">
                    <div class="help-section">
                        <h4>🔗 Approval Workflows</h4>
                        <p><strong>How workflows are automatically attached to your leave:</strong></p>
                        
                        <div class="grid">
                            <div class="col-md-6">
                                <h6>Automatic Selection:</h6>
                                <ul>
                                    <li><strong>1-7 days Annual:</strong> Manager approval (48h)</li>
                                    <li><strong>8+ days Annual:</strong> Manager → RSM approval</li>
                                    <li><strong>Medical Leave:</strong> Manager + HR (parallel)</li>
                                    <li><strong>Emergency:</strong> Auto-approved immediately</li>
                                    <li><strong>Mandatory:</strong> Auto-approved (BFSI compliance)</li>
                                </ul>
                            </div>
                            <div class="col-md-6">
                                <h6>Workflow Features:</h6>
                                <ul>
                                    <li><strong>Smart Selection:</strong> Based on type + duration</li>
                                    <li><strong>Transparent Process:</strong> See approval steps</li>
                                    <li><strong>Timeout Protection:</strong> Auto-escalation</li>
                                    <li><strong>Delegation Support:</strong> Proxy approvers</li>
                                    <li><strong>Compliance Built-in:</strong> Regulatory requirements</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div class="workflow-examples mt-3">
                            <h6>Example Workflow Process:</h6>
                            <div class="workflow-example">
                                <strong>3-Day Annual Leave:</strong>
                                <div class="workflow-steps">
                                    Submit → Manager Review (48h) → Approved ✅
                                </div>
                            </div>
                            <div class="workflow-example">
                                <strong>10-Day Annual Leave:</strong>
                                <div class="workflow-steps">
                                    Submit → Manager (48h) → RSM (72h) → Approved ✅
                                </div>
                            </div>
                            <div class="workflow-example">
                                <strong>Emergency Leave:</strong>
                                <div class="workflow-steps">
                                    Submit → Auto-Approved ⚡ (with notification)
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-12">
                    <div class="help-section">
                        <h4>🛠️ Troubleshooting</h4>
                        <div class="grid">
                            <div class="col-md-6">
                                <h6>Common Issues:</h6>
                                <ul>
                                    <li><strong>"Insufficient balance":</strong> Check your available leave days</li>
                                    <li><strong>"Team coverage low":</strong> Choose alternative dates</li>
                                    <li><strong>"Documentation required":</strong> Upload supporting documents</li>
                                    <li><strong>"Workflow not found":</strong> Contact admin for workflow setup</li>
                                </ul>
                            </div>
                            <div class="col-md-6">
                                <h6>Getting Help:</h6>
                                <ul>
                                    <li><strong>Technical Issues:</strong> Contact IT Helpdesk</li>
                                    <li><strong>Leave Policies:</strong> Contact HR Department</li>
                                    <li><strong>Workflow Questions:</strong> Check Admin → Approval Flows</li>
                                    <li><strong>System Training:</strong> Contact your manager</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-content-between">
                <div>
                    <small class="text-muted">SimForce Enhanced Leave Management System</small>
                </div>
                <div>
                    <p-button label="Close" @click="showHelpDialog = false" />
                </div>
            </div>
        </template>
    </p-dialog>

  </template>

  <script lang="ts">
  import { Options } from "vue-class-component";
  import Menubar from "primevue/menubar";
  import SandBox from "./perms/sandbox.vue";
  import { base } from "../misc/base";
  import Cookies from 'js-cookie';
  import Badge from 'primevue/badge';

  // Mock components if web-sdk not available
  let Halo: any;
  let GetSession: any;

  // Using dynamic import with import() function which returns a Promise
  // This is allowed by ESLint as it's part of standard ES modules
  const importWebSdk = async () => {
    try {
      // Using dynamic import for the module
      const webSdk = await import("web-sdk").catch(() => null);
      if (webSdk) {
        Halo = webSdk.Halo;
        GetSession = webSdk.GetSession;

        // Handle styles manually since dynamic CSS imports can cause TS issues
        // This approach avoids the TypeScript error with CSS imports
        try {
          // Either the web-sdk is local or doesn't exist
          // Instead of trying to load from CDN (which failed with 404),
          // we'll just create a minimal inline style for any required elements
          console.log('Creating minimal styles for web-sdk components');
          const style = document.createElement('style');
          style.textContent = `
            /* Minimal styles for web-sdk components */
            .web-sdk-halo {
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto;
              padding: 8px;
            }
            .web-sdk-avatar {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background-color: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 8px;
              font-weight: bold;
              color: #333;
            }
          `;
          document.head.appendChild(style);
          console.log('Applied minimal web-sdk styles');
        } catch (e) {
          console.warn("Failed to load web-sdk styles:", e);
        }
        return true;
      }
      return false;
    } catch (e) {
      console.warn("web-sdk not available, using mock components");
      return false;
    }
  };

  // Set up mock components as fallback
  Halo = {
    render() {
      return null;
    }
  };

  // Mock GetSession using the authenticated user data from localStorage
  GetSession = () => {
    try {
      const user = localStorage.getItem('user') ?
        JSON.parse(localStorage.getItem('user') || '{}') : {};
      return {
        services: ['admin'],
        roles: ['ADMIN'],
        user
      };
    } catch (e) {
      console.error("Error in mock GetSession:", e);
      return { services: [], roles: [], user: {} };
    }
  };

  // Try to import web-sdk (async)
  importWebSdk().then(success => {
    if (success) {
      console.log("Successfully loaded web-sdk");
    }
  });

  @Options({
    components:{
        Menubar,
        Halo,
        SandBox,
        Badge,
    }
  })
  export default class NavBar extends base {
        loginDialog = false

        sandboxDialog = false
        messengerDialog = false
        sidebarVisible = false
        adminExpanded = true
        showHelpDialog = false
        home_url = process.env.VUE_APP_SIMUAM || 'http://localhost:9080'
        env = process.env.NODE_ENV || 'development';

        /**
         * Reactive auth state from Vuex
         */
        get isSignedIn(): boolean {
            return this.store.getters.isSignedIn;
        }

        get isAdminUser(): boolean {
            const user = this.store.getters.currentUser || {};
            const role = user.role || '';
            console.log('Current user:', user);
            console.log('User role:', role);
            // Check for various admin role formats
            const isAdmin = role.toLowerCase() === 'admin' ||
                   role.toUpperCase() === 'ADMIN' ||
                   role === 'Admin' ||
                   role === 'admin1' ||
                   role.toLowerCase().includes('admin');
            console.log('Is admin?', isAdmin);
            return isAdmin;
        }

        /**
         * Current user from Vuex store
         */
        get user() {
            return this.store.getters.currentUser || {};
        }

        /**
         * Build menu model reactively
         */
        get mainMenuItems(): any[] {
            return [
                { label: 'Dashboard', icon: 'pi pi-fw pi-chart-pie', to: '/' },
                { label: 'Business Dashboard', icon: 'pi pi-fw pi-chart-bar', to: '/business-dashboard' },
                { label: 'Location Tracking', icon: 'pi pi-fw pi-map-marker', to: '/location-tracking' },
                { label: 'Leave Management', icon: 'pi pi-fw pi-calendar', to: '/leave-management' },
                { label: 'Leads', icon: 'pi pi-fw pi-crown', to: '/leads' },
                { label: 'Lead Analytics', icon: 'pi pi-fw pi-chart-bar', to: '/lead-analytics' },
                { label: 'Lead Intelligence', icon: 'pi pi-fw pi-sparkles', to: '/leads/intelligence' },
                { label: 'Assignment Config', icon: 'pi pi-fw pi-cog', to: '/assignment-config' },
                { label: 'Customers', icon: 'pi pi-fw pi-users', to: '/customers' },
            ];
        }

        get adminMenuItems(): any[] {
            return [
                { label: 'Products', icon: 'pi pi-fw pi-box', to: '/products' },
                { label: 'Product Catalog', icon: 'pi pi-fw pi-book', to: '/product-catalog' },
                { label: 'Targets', icon: 'pi pi-fw pi-bullseye', to: '/targets' },
                { label: 'Business Rules', icon: 'pi pi-fw pi-bolt', to: '/business_rules' },
                { label: 'Intelligence Config', icon: 'pi pi-fw pi-chart-line', to: '/intelligence-config' },
                { label: 'Branches', icon: 'pi pi-fw pi-building', to: '/branches' },
                { label: 'Users', icon: 'pi pi-fw pi-user', to: '/users' },
                { label: 'Employees', icon: 'pi pi-fw pi-id-card', to: '/employees' },
                { label: 'Vendors', icon: 'pi pi-fw pi-briefcase', to: '/vendors' },
                { label: 'Contract Staff', icon: 'pi pi-fw pi-users', to: '/contract-staff' },
                { label: 'Organization Hierarchy', icon: 'pi pi-fw pi-sitemap', to: '/hierarchy' },
                { label: 'Hierarchy Management', icon: 'pi pi-fw pi-share-alt', to: '/hierarchy-management' },
                { label: 'Vendor Attendance Reports', icon: 'pi pi-fw pi-calendar', to: '/vendor-attendance-reports' },
                { label: 'Role Management', icon: 'pi pi-fw pi-shield', to: '/role-management' },
                // Verification Management
                { label: 'Verification Templates', icon: 'pi pi-fw pi-check-circle', to: '/verification-templates' },
                { label: 'Verification Dashboard', icon: 'pi pi-fw pi-chart-line', to: '/verification-dashboard' },
                { label: 'File Tagging Demo', icon: 'pi pi-fw pi-tags', to: '/verification-file-demo' },
                // Enhanced Leave Management
                { label: 'Leave Rules', icon: 'pi pi-fw pi-cog', to: '/leave-rules' },
                { label: 'Approval Flows', icon: 'pi pi-fw pi-share-alt', to: '/approval-flows' },
                { label: 'Leave Analytics', icon: 'pi pi-fw pi-chart-line', to: '/leave-analytics' },
                // Employee Lifecycle Management
                { label: 'Employee Lifecycle', icon: 'pi pi-fw pi-refresh', to: '/employee-lifecycle' },
                // Geography Management
                { label: 'States', icon: 'pi pi-fw pi-map', to: '/states' },
                { label: 'Districts', icon: 'pi pi-fw pi-map-marker', to: '/districts' },
                { label: 'Cities', icon: 'pi pi-fw pi-building', to: '/cities' },
                { label: 'Pincodes', icon: 'pi pi-fw pi-send', to: '/pincodes' },
                // Offline Configuration Management
                { label: 'Offline Settings', icon: 'pi pi-fw pi-wifi', to: '/offline/settings' },
                { label: 'Offline Monitoring', icon: 'pi pi-fw pi-chart-line', to: '/offline/monitoring' },
                { label: 'Offline Audit', icon: 'pi pi-fw pi-history', to: '/offline/audit' },
                { label: 'System Initialization', icon: 'pi pi-fw pi-cog', to: '/system-initialization' },
                { label: 'Authorization Models', icon: 'pi pi-fw pi-lock', to: '/authorization_models' },
                { label: 'Masters', icon: 'pi pi-fw pi-briefcase', to: '/masters' },
            ];
        }

        toggleSidebar() {
            this.sidebarVisible = !this.sidebarVisible;
        }


        logout(){
            // Store user ID for the API call before clearing storage
            const userId = window.localStorage["userId"];
            if (!userId) {
                console.error("No user ID found for logout request");
            }
            const token = Cookies.get("API_TOKEN") || window.localStorage.getItem("user_token") || "";

            // Clear localStorage
            window.localStorage.clear()

            // Clear cookies including API_TOKEN
            Cookies.remove('API_TOKEN')

            // Remove from sessionStorage too just to be thorough
            sessionStorage.clear();

            // Try to call backend logout endpoint if it exists
            try {
                // Get API URL from environment or default
                const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:9006';
                console.log('Logging out from API URL:', apiUrl);

                // Create a simple fetch request to logout with all required headers
                // No longer sending user ID in headers - authentication is via token only
                fetch(`${apiUrl}/api/v1/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-TOKEN': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
                    }
                }).catch(err => {
                    console.log('Logout API may not be available:', err);
                });
            } catch (error) {
                console.log('Error during logout:', error);
            }

            // Redirect to login page
            window.location = "/login" as any
        }

        openUserManual() {
            // Show inline help dialog instead of opening external file
            this.showHelpDialog = true;
        }
  }
  </script>

  <style scoped>
  /* Top Bar Styles */
  .top-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;
    z-index: 1000;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .top-bar-start {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .top-bar-end {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logo-container {
    text-decoration: none;
  }

  .sidebar-toggle {
    color: var(--primary-color) !important;
  }

  /* Sidebar Styles */
  .sidebar {
    position: fixed;
    top: 60px;
    left: 0;
    width: 280px;
    height: calc(100vh - 60px);
    background: var(--surface-card);
    border-right: 1px solid var(--surface-border);
    transform: translateX(0);
    transition: transform 0.3s ease;
    z-index: 999;
    overflow-y: auto;
    box-shadow: 2px 0 12px 0 rgba(0, 0, 0, 0.1);
  }

  .sidebar-collapsed {
    transform: translateX(-280px);
  }

  .sidebar-content {
    padding: 1rem 0;
  }

  .menu-section {
    margin-bottom: 1rem;
  }

  .menu-header {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    font-weight: 600;
    color: var(--text-color-secondary);
    border-bottom: 1px solid var(--surface-border);
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .menu-header:hover {
    background-color: var(--surface-hover);
  }

  .menu-label {
    flex: 1;
    margin-left: 0.75rem;
  }

  .toggle-icon {
    transition: transform 0.3s ease;
  }

  .toggle-icon.expanded {
    transform: rotate(180deg);
  }

  .menu-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    text-decoration: none;
    color: var(--text-color);
    transition: all 0.2s;
    border-left: 3px solid transparent;
  }

  .menu-item:hover {
    background-color: var(--surface-hover);
    color: var(--primary-color);
  }

  .menu-item.active {
    background-color: var(--primary-color-text);
    color: var(--primary-color);
    border-left-color: var(--primary-color);
    font-weight: 600;
  }

  .submenu {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    overflow-x: hidden;
    transition: max-height 0.3s ease;
  }

  .submenu-collapsed {
    max-height: 0;
  }

  .submenu-item {
    padding-left: 3rem;
    background-color: var(--surface-ground);
  }

  .submenu-item:hover {
    background-color: var(--surface-hover);
  }

  /* Sidebar Overlay */
  .sidebar-overlay {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 998;
  }

  /* User Section */
  .user-section {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .user-name {
    font-weight: 600;
    color: var(--primary-color);
    margin-right: 0.5rem;
  }

  /* Mobile Responsiveness */
  @media (max-width: 768px) {
    .sidebar {
      width: 100%;
      transform: translateX(-100%);
    }
    
    .sidebar-collapsed {
      transform: translateX(-100%);
    }
    
    .sidebar:not(.sidebar-collapsed) {
      transform: translateX(0);
    }

    .user-section {
      gap: 0.25rem;
    }

    .user-name {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .top-bar {
      padding: 0 0.5rem;
    }
    
    .top-bar-start {
      gap: 0.5rem;
    }
    
    .top-bar-end {
      gap: 0.25rem;
    }
  }

  /* Help Dialog Styles */
  .help-content {
    max-height: 70vh;
    overflow-y: auto;
    padding: 20px;
  }

  .help-section {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    height: 100%;
  }

  .help-section h4 {
    color: #495057;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  .help-section h6 {
    color: #6c757d;
    margin-bottom: 10px;
    font-size: 1rem;
  }

  .help-section ul,
  .help-section ol {
    padding-left: 20px;
    line-height: 1.6;
  }

  .help-section li {
    margin-bottom: 8px;
  }

  .text-muted {
    color: #6c757d;
  }

  .workflow-examples {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 15px;
    border-left: 3px solid #007bff;
  }

  .workflow-example {
    margin-bottom: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;
  }

  .workflow-example:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .workflow-steps {
    font-family: 'Courier New', monospace;
    background: #e9ecef;
    padding: 8px 12px;
    border-radius: 4px;
    margin-top: 5px;
    font-size: 0.9rem;
    color: #495057;
  }
  </style>